import React, { useState } from "react";
import { ArrowRight, Bell, Activity, Zap, ShieldCheck, BarChart3, Users, Sparkles, CheckCircle2, Award, Layers } from "lucide-react";
import { stats, platformFeatures, clients } from "../mock";
import ScheduleDemoCTA from "../components/ScheduleDemoCTA";

const HeroVisual = () => (
  <div className="relative w-full max-w-[560px] mx-auto">
    <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full -z-10" style={{ background: "radial-gradient(circle, #eaf1ff 0%, transparent 70%)" }} />
    <div className="absolute top-10 right-0 w-56 h-56 rounded-full -z-10" style={{ background: "radial-gradient(circle, #fff1ea 0%, transparent 70%)" }} />

    <img
      src="/images/hero-dashboard.png"
      alt="Somm.ai dashboard with account intelligence"
      className="w-full h-auto rounded-2xl"
    />

    <img
      src="/images/prestige-score.png"
      alt="Account prestige score widget"
      className="hidden sm:block absolute -right-8 md:-right-12 top-1/3 w-44 md:w-52 drop-shadow-2xl rounded-xl"
    />

    <img
      src="/images/median-price.png"
      alt="Median price trend widget"
      className="hidden sm:block absolute -left-8 md:-left-12 -bottom-8 w-52 md:w-60 drop-shadow-2xl rounded-xl"
    />
  </div>
);

const SectionHeader = ({ eyebrow, title, desc, light }) => (
  <div className="max-w-3xl">
    <div className="eyebrow">{eyebrow}</div>
    <h2 className={`mt-3 text-4xl md:text-5xl font-extrabold tracking-tight ${light ? "text-white" : ""}`}>{title}</h2>
    {desc && <p className={`mt-4 text-lg ${light ? "text-white/80" : "text-[#4b5566]"}`}>{desc}</p>}
  </div>
);

const SALES_TABS = [
  {
    key: "peer",
    title: "Peer Brands",
    desc: "Find accounts that carry your peer brands but do not carry your brand",
    icon: Users,
    image: "/images/peer-brands.png",
    accent: { bg: "#eaf1ff", fg: "#2552ff" },
  },
  {
    key: "prestige",
    title: "Account Prestige",
    desc: "Target high prestige accounts to ensure quality of distribution. In addition to Awards (e.g. Michelin Star), we have our own proprietary ratings of accounts",
    icon: Award,
    image: "/images/prestige.png",
    accent: { bg: "#e8faef", fg: "#16a34a" },
  },
  {
    key: "alignment",
    title: "Distributor Alignment",
    desc: "Target accounts that already work with your distributor in a particular market",
    icon: ShieldCheck,
    image: "/images/dist-alignment.png",
    accent: { bg: "#fff1ea", fg: "#f97316" },
  },
];

export default function Home() {
  const [activeTab, setActiveTab] = useState(SALES_TABS[0].key);
  const active = SALES_TABS.find((t) => t.key === activeTab) ?? SALES_TABS[0];

  return (
    <div className="bg-subtle">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-28 grid md:grid-cols-2 gap-16 items-center">
        <div>
          <h1 className="hero-title mt-6 text-5xl md:text-7xl">On-Premise<br/>Intelligence!</h1>
          <p className="mt-6 text-lg md:text-xl text-[#4b5566] max-w-xl">
            Leverage the world's largest updating database of wine and spirits lists to improve on-premise sales
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="https://www.somm.ai/meeting" target="_blank" rel="noreferrer" className="btn-green">Schedule a Demo <ArrowRight size={18} /></a>
          </div>
          <div className="mt-10 flex flex-wrap gap-8">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "#fff1ea", color: "#f97316" }}><Activity size={20}/></div>
              <div className="font-semibold">Actionable Data</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "#eaf1ff", color: "#2552ff" }}><Zap size={20}/></div>
              <div className="font-semibold">Real-time Insights</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "#e8faef", color: "#16a34a" }}><Bell size={20}/></div>
              <div className="font-semibold">Smart Alerts</div>
            </div>
          </div>
        </div>
        <div className="relative pl-2 pr-4 pb-16">
          <HeroVisual />
        </div>
      </section>

      {/* Trusted source */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionHeader eyebrow="The leader in wine & spirits intel" title="The Largest and Most Trusted Source of Data" desc="Our rich and timely information on your customer's changing wine and spirits list helps you see sales opportunities as they arise" />
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {stats.map((s) => (
            <div key={s.label} className="card-soft p-8">
              <div className="text-5xl font-extrabold text-[#0b1526]">{s.value}</div>
              <div className="text-[#7a8499] mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Benchmarking */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-14 items-center">
        <div className="relative order-2 md:order-1">
          <div className="absolute -top-8 -left-8 w-48 h-48 rounded-full -z-10" style={{ background: "radial-gradient(circle, #eaf1ff 0%, transparent 70%)" }} />
          <img
            src="/images/trends-graph.png"
            alt="Brand performance trends graph in Somm.ai"
            className="w-full h-auto rounded-2xl"
          />
          <img
            src="/images/dist-comp.png"
            alt="Distributor comparison product screenshot"
            className="hidden sm:block absolute -right-4 -bottom-8 w-40 md:w-52 drop-shadow-2xl rounded-xl"
          />
        </div>
        <div className="order-1 md:order-2">
          <SectionHeader eyebrow="Benchmarking" title="Assess On-Premise Brand Performance" desc="Find the top brands or SKUs in any market or sliver of the on-premise market." />
          <ul className="mt-8 space-y-4">
            {[
              { icon: BarChart3, t: "Compare Distributors", d: "Find out if distributors are outperforming or underperforming" },
              { icon: Layers, t: "Measure Share Gain", d: "See which brands or SKUs are gaining or losing share in a market" },
            ].map(({ icon: Icon, t, d }) => (
              <li key={t} className="flex gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#eaf1ff", color: "#2552ff" }}><Icon size={18}/></div>
                <div><div className="font-semibold">{t}</div><div className="text-[#4b5566] text-sm mt-1">{d}</div></div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Sales - Qualified Leads with product tabs */}
      <section className="relative py-20" style={{ background: "#0b1526" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="eyebrow">Sales</div>
            <h2 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight text-white">Find Qualified On-Premise Leads</h2>
            <p className="mt-4 text-lg text-white/80">Drive improved on-premise distribution with clear targets that are most likely to convert. Send lists to distributor partners or your own sales team</p>
          </div>

          <div className="mt-14 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5 space-y-3">
              {SALES_TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = tab.key === activeTab;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`w-full text-left p-6 rounded-2xl transition-all border ${isActive ? "bg-white/10 border-white/20 shadow-2xl" : "bg-transparent border-transparent hover:bg-white/5"}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: tab.accent.bg, color: tab.accent.fg }}>
                        <Icon size={18} />
                      </div>
                      <div className="font-bold text-lg text-white">{tab.title}</div>
                    </div>
                    <div className="text-sm text-white/70">{tab.desc}</div>
                  </button>
                );
              })}
            </div>

            <div className="md:col-span-7 relative">
              <div className="absolute -top-10 -left-10 w-56 h-56 rounded-full -z-0" style={{ background: "radial-gradient(circle, rgba(37,82,255,0.35) 0%, transparent 70%)" }} />
              <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full -z-0" style={{ background: "radial-gradient(circle, rgba(249,115,22,0.35) 0%, transparent 70%)" }} />
              <img
                key={active.key}
                src={active.image}
                alt={`Somm.ai ${active.title} product screenshot`}
                className="relative w-full h-auto block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marketing */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-14 items-center">
        <div className="relative order-2 md:order-1">
          <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full -z-10" style={{ background: "radial-gradient(circle, #fff1ea 0%, transparent 70%)" }} />
          <img
            src="/images/michelin.png"
            alt="Account detail with Michelin star indicator"
            className="w-full h-auto rounded-2xl"
          />
          <img
            src="/images/popular-regions.png"
            alt="Popular wine regions widget"
            className="hidden sm:block absolute -left-6 -bottom-6 w-40 md:w-52 drop-shadow-2xl rounded-xl"
          />
        </div>
        <div className="order-1 md:order-2">
          <SectionHeader eyebrow="Marketing" title="Datapoints to nail your sales pitch" desc="Include our trusted data in presentations to convince buyers on the strength of your brand" />
          <ul className="mt-8 space-y-4">
            {[
              { t: "RFPs", d: "Use our data to show national account buyers your strengths" },
              { t: "Alternative Data", d: "Use our proprietary data to place your company in the best light" },
            ].map(({ t, d }) => (
              <li key={t} className="flex gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#e8faef", color: "#16a34a" }}><CheckCircle2 size={18}/></div>
                <div><div className="font-semibold">{t}</div><div className="text-[#4b5566] text-sm mt-1">{d}</div></div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* All-in-one */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionHeader eyebrow="All you can eat" title="An all-in-one intelligence platform." desc="Somm.ai subscriptions include unlimited users and unlimited data on all brands and all markets" />
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {platformFeatures.map((f) => (
            <div key={f} className="card-soft p-6 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#eaf1ff", color: "#2552ff" }}><Sparkles size={18}/></div>
              <div className="font-medium">{f}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Clients */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <SectionHeader eyebrow="Clients" title="Don't take our word for it." desc="Our clients are highly-respected brands, importers and distributors of all sizes" />
        <div className="marquee mt-12">
          <div className="marquee-track">
            {[...clients, ...clients].map((c, i) => (
              <img key={i} src={c.logo} alt={c.name} className="h-10 md:h-12 object-contain grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all" />
            ))}
          </div>
        </div>
      </section>

      <ScheduleDemoCTA />
    </div>
  );
}
